import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { initializeTheme, initializeSnow } from './components/effects'
import { BrandLogo } from './components/shared'
import { assetOrigin, loadData } from './components/load-data'
import summary from './data/compiled/summary.json'
import favicon from './favicon.svg'
import './components/vendor/leaflet.css'
import './hangzhou.css'

const mobileQuery =
  '(max-width: 680px), (max-width: 1000px) and (max-height: 500px) and (pointer: coarse)'
const layerOptions = [
  ['tracks', '轨迹'],
  ['points', '照片点'],
  ['labels', '区县名称'],
  ['context', '道路与水系'],
  ['places', '关注地点'],
  ['rawTracks', '原始轨迹'],
]
const initialLayers = {
  tracks: true,
  points: true,
  labels: true,
  context: true,
  places: true,
  rawTracks: false,
}
const initialUI = {
  ready: false,
  total: summary.stats.all.total,
  days: summary.stats.all.days,
  counts: summary.stats.all.counts,
  features: summary.features,
  years: summary.years,
  district: -1,
  view: 'urban',
  year: 'all',
  layers: initialLayers,
  availableLayers: {},
  zoomInDisabled: false,
  zoomOutDisabled: false,
}
const format = (value) => (value === null ? '—' : value.toLocaleString('zh-CN'))

function PhotoSymbol() {
  return (
    <svg className="point" viewBox="-1.2 -1.2 2.4 2.4" aria-hidden="true">
      <use href="#photo-petals" />
      <circle className="photo-heart" r=".2" />
    </svg>
  )
}

function Overview({ total, days, idPrefix = '' }) {
  return (
    <section className="overview">
      <div className="record-total">
        <div className="stat-value">
          <strong id={`${idPrefix}point-count`} aria-live="polite">
            {total === null ? '—' : total}
          </strong>
          <span className="stat-unit">条</span>
        </div>
        <span>定位记录</span>
      </div>
      <div className="secondary-stats">
        <div>
          <div className="stat-value">
            <strong id={`${idPrefix}day-count`}>{format(days)}</strong>
            <span className="stat-unit">天</span>
          </div>
          <span>记录天数</span>
        </div>
      </div>
    </section>
  )
}

export default function HangzhouPage() {
  const root = useRef(null)
  const controls = useRef(null)
  const [ui, setUI] = useState(initialUI)
  const [mobile, setMobile] = useState(false)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    const documentRoot = document.documentElement
    const previousTheme = documentRoot.getAttribute('data-theme')
    const stopTheme = initializeTheme()
    const stopSnow = initializeSnow()
    const media = window.matchMedia(mobileQuery)
    const update = () => setMobile(media.matches)
    update()
    media.addEventListener('change', update)
    return () => {
      media.removeEventListener('change', update)
      stopTheme()
      if (stopSnow) stopSnow()
      if (previousTheme === null) documentRoot.removeAttribute('data-theme')
      else documentRoot.setAttribute('data-theme', previousTheme)
      for (const property of [
        '--theme-origin-x',
        '--theme-origin-y',
        '--theme-feather',
        '--theme-header-bottom',
      ])
        documentRoot.style.removeProperty(property)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    setError('')
    setUI(initialUI)
    let initializing = false
    Promise.all([
      loadData(controller.signal),
      import('./components/map').catch(() => {
        throw new Error('地图组件未能加载，请重新加载。')
      }),
    ])
      .then(([data, module]) => {
        if (controller.signal.aborted) return
        initializing = true
        controls.current = module.default(root.current, data, (changes) => {
          if (!controller.signal.aborted)
            setUI((current) => ({
              ...current,
              ...changes,
              features: data.districts.features,
            }))
        })
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setUI(initialUI)
          setError(
            initializing ? '地图初始化失败，请重新加载。' : error.message
          )
        }
      })
    return () => {
      controller.abort()
      if (controls.current) controls.current.dispose()
      controls.current = null
    }
  }, [attempt])

  const act = (name, ...args) => {
    if (controls.current) controls.current[name](...args)
  }
  const yearsLabel = ui.years.length
    ? `${ui.years[ui.years.length - 1]} — ${ui.years[0]}`
    : '—'
  const districtOrder = ui.counts
    .map((_, index) => index)
    .sort((a, b) => ui.counts[b] - ui.counts[a] || a - b)
  const maximum = Math.max(...ui.counts, 1)

  return (
    <div ref={root} className="HangzhouPage">
      <Helmet
        defer={false}
        htmlAttributes={{ lang: 'zh-CN', class: 'HangzhouDocument' }}
      >
        <title>给时间 - doubleZ的杭州足迹</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        <meta name="color-scheme" content="light" />
        <meta
          name="description"
          content="doubleZ的杭州足迹，杭州全域 GPS 轨迹与照片定位的交互地图。"
        />
        <link rel="canonical" href="https://www.doublez.site/hangzhou/" />
        <link rel="icon" type="image/svg+xml" href={favicon} />
        <link rel="preconnect" href={assetOrigin} crossOrigin="anonymous" />
      </Helmet>
      <Symbols />
      <Header yearsLabel={yearsLabel} />
      <main className="workspace">
        <section className="map-stage" aria-label="杭州足迹交互地图">
          <div
            id="map"
            tabIndex="0"
            aria-label={
              mobile
                ? '杭州地图。点击区县定位，使用右下角按钮缩放；上下滑动浏览页面。'
                : '杭州地图。方向键平移，加减键缩放；也可使用右下角地图按钮。'
            }
          />
          <div className="mobile-shoreline">
            <Landscape idPrefix="mobile-" />
            <Overview total={ui.total} days={ui.days} idPrefix="mobile-" />
          </div>
          <section
            id="district-grid"
            className="district-grid"
            aria-label="区县棋盘，按足迹数量从多到少排列"
            tabIndex="0"
            hidden
          />
          <div className="map-toolbar" aria-label="地图视角">
            <div className="view-switch">
              {[
                ['all', '杭州全域'],
                ['urban', '主城区'],
                ['grid', '区县棋盘'],
              ].map(([view, label]) => (
                <button
                  key={view}
                  type="button"
                  id={`view-${view}`}
                  disabled={!ui.ready}
                  onClick={() => act(view)}
                  aria-pressed={ui.view === view}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="map-tools" aria-label="地图缩放与比例尺">
            <div className="map-zoom" aria-label="地图缩放">
              <button
                type="button"
                id="zoom-in"
                disabled={!ui.ready || ui.zoomInDisabled}
                onClick={() => act('zoomIn')}
                className="square"
                title="放大地图"
                aria-label="放大地图"
              >
                ＋
              </button>
              <button
                type="button"
                id="zoom-out"
                disabled={!ui.ready || ui.zoomOutDisabled}
                onClick={() => act('zoomOut')}
                className="square"
                title="缩小地图"
                aria-label="缩小地图"
              >
                −
              </button>
            </div>
            <div id="map-scale" className="map-scale" aria-label="地图比例尺" />
          </div>
          <div className="north" aria-hidden="true">
            <span>N</span>
            <svg viewBox="0 0 20 32">
              <path d="M10 2L17 28L10 22L3 28Z" />
            </svg>
          </div>
          <div
            id="map-status"
            className={`map-status${error ? '' : ' map-status-pending'}`}
            role="status"
            hidden={ui.ready && !error}
          >
            {error ? (
              <>
                {error}
                <button
                  type="button"
                  onClick={() => setAttempt((value) => value + 1)}
                >
                  重新加载
                </button>
              </>
            ) : (
              '正在加载地图…'
            )}
          </div>
          <div className="legend" aria-label="地图图例">
            <span>
              <i className="line" title="经过越多越亮" />
              <span id="track-legend">轨迹</span>
            </span>
            <span>
              <PhotoSymbol />
              照片点
            </span>
            <span>
              <i className="border" />
              行政边界
            </span>
          </div>
        </section>
        <aside className="sidebar" aria-label="足迹筛选与统计">
          <div className="sidebar-first-page">
            <section className="year-section">
              <label htmlFor="year">时间范围</label>
              <select
                id="year"
                disabled={!ui.ready}
                value={ui.year}
                onChange={(event) => act('year', event.target.value)}
              >
                <option value="all">全部年份</option>
                {ui.years.map((year) => (
                  <option key={year} value={year}>
                    {year} 年
                  </option>
                ))}
              </select>
            </section>
            <div className="sidebar-summary">
              <section className="district-section">
                <div className="section-heading">
                  <h2>区县足迹</h2>
                </div>
                <div id="district-list" className="district-list">
                  {districtOrder.map((index) => (
                    <button
                      key={index}
                      className={`district-button${
                        ui.counts[index] ? '' : ' no-records'
                      }`}
                      type="button"
                      data-district={index}
                      disabled={!ui.ready}
                      style={{
                        '--bar': `${(ui.counts[index] / maximum) * 100}%`,
                      }}
                      aria-pressed={ui.district === index}
                      aria-label={`${
                        ui.features[index].properties.name
                      }，${format(ui.counts[index])} 条记录`}
                      onClick={() => act('district', index)}
                    >
                      <span>{ui.features[index].properties.name}</span>
                      <span className="district-number">
                        {format(ui.counts[index])}
                      </span>
                    </button>
                  ))}
                </div>
                <p
                  id="empty-message"
                  className="empty-message"
                  hidden={!ui.ready || ui.total > 0}
                >
                  这一年在该区县没有记录。可切换年份，或点击“杭州全域”。
                </p>
              </section>
              <Overview total={ui.total} days={ui.days} />
            </div>
            <Landscape />
          </div>
          <section className="layers">
            <h2>地图图层</h2>
            <div className="layer-grid">
              {layerOptions.map(([key, label]) => (
                <label
                  key={key}
                  className={
                    key === 'rawTracks' ? 'raw-track-option' : undefined
                  }
                  title={
                    ui.availableLayers[key] === false
                      ? '图层数据未加载，请更新数据后刷新。'
                      : key === 'places'
                      ? '放大地图后显示'
                      : undefined
                  }
                >
                  <input
                    type="checkbox"
                    id={`show-${key === 'rawTracks' ? 'raw-tracks' : key}`}
                    disabled={
                      !ui.ready ||
                      ui.availableLayers[key] === false ||
                      (key === 'rawTracks' && !ui.layers.tracks)
                    }
                    checked={ui.layers[key]}
                    onChange={(event) =>
                      act('layer', key, event.target.checked)
                    }
                  />
                  {key === 'tracks' && <i className="line" />}
                  {key === 'points' && <PhotoSymbol />}
                  {label}
                  {ui.availableLayers[key] === false && '（未加载）'}
                </label>
              ))}
            </div>
          </section>
        </aside>
      </main>
      <noscript>请在浏览器中启用 JavaScript 以查看交互地图。</noscript>
    </div>
  )
}

const Header = React.memo(function Header({ yearsLabel }) {
  return (
    <header className="page-header">
      <div className="identity">
        <button
          className="brand-weather"
          type="button"
          disabled
          aria-label="暂停落雪"
          aria-pressed="false"
          title="暂停落雪"
        >
          <BrandLogo />
        </button>
        <div>
          <h1>doubleZ的杭州足迹</h1>
        </div>
      </div>
      <span className="header-years" aria-label={`足迹年份：${yearsLabel}`}>
        {yearsLabel}
      </span>
      <button
        id="theme-toggle"
        className="theme-toggle"
        type="button"
        disabled
        aria-label="切换到深色模式"
        aria-pressed="true"
        title="切换到深色模式 · 三潭印月"
      >
        <svg className="theme-scene" viewBox="0 0 64 48" aria-hidden="true">
          <defs>
            <clipPath id="theme-sky">
              <path d="M3 1H61V34H3Z"></path>
            </clipPath>
          </defs>
          <g className="theme-sky" clip-path="url(#theme-sky)">
            <g className="theme-sun" transform="translate(42.57 13.15)">
              <circle r="6"></circle>
            </g>
            <g className="theme-moon" transform="translate(21.43 54.85)">
              <path d="M3.5-6.1A7 7 0 1 0 6.5 2.6A6.2 6.2 0 0 1 3.5-6.1Z"></path>
            </g>
          </g>
          <g className="theme-towers" fill="currentColor">
            <path
              id="theme-tower"
              d="M29 33v-3h1v-2c-4-2-4-7 0-9v-3h-3l4-4 1-4 1 4 4 4h-3v3c4 2 4 7 0 9v2h1v3Zm2-11v3h2v-3Z"
              fillRule="evenodd"
            ></path>
            <use
              href="#theme-tower"
              transform="translate(-17 10) scale(.77)"
            ></use>
            <use
              href="#theme-tower"
              transform="translate(28 10) scale(.77)"
            ></use>
          </g>
          <g
            className="theme-ripples"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
          >
            <path d="M6 36q7-2 14 0m5 0q7-2 14 0m5 0q7-2 14 0"></path>
            <path d="M11 40h5m12 0h8m13 0h5M30 44h4"></path>
          </g>
        </svg>
      </button>
    </header>
  )
})

function Landscape({ idPrefix = '' }) {
  return (
    <svg
      className="shoreline"
      viewBox="0 0 360 230"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`${idPrefix}mountain-wash`} x2="0" y2="1">
          <stop className="mountain-top"></stop>
          <stop offset="1" className="mountain-base"></stop>
        </linearGradient>
        <linearGradient id={`${idPrefix}mountain-mist`} x2="0" y2="1">
          <stop stopColor="white" stopOpacity="0"></stop>
          <stop offset=".25" stopColor="white" stopOpacity=".8"></stop>
          <stop offset="1" stopColor="white"></stop>
        </linearGradient>
        <mask id={`${idPrefix}mountain-fade`}>
          <rect
            width="360"
            height="230"
            fill={`url(#${idPrefix}mountain-mist)`}
          ></rect>
        </mask>
      </defs>
      <g mask={`url(#${idPrefix}mountain-fade)`}>
        <path
          className="far-ranges"
          d="M-20 157C4 155 10 133 28 125C44 118 46 98 57 90C68 81 71 58 84 55C95 52 98 71 109 80C119 88 128 90 140 108C152 126 156 160 174 160C194 160 214 128 231 125C245 122 254 138 266 150C279 162 292 165 307 168C326 173 338 162 352 166C365 170 374 179 386 183V240H-20Z"
        ></path>
        <path
          className="middle-ranges"
          d="M-20 197C1 180 12 174 28 155C40 141 49 144 59 127C69 111 76 113 84 94C89 83 91 74 99 73C109 71 113 89 121 98C130 109 137 109 145 124C154 141 166 177 178 170C190 162 203 151 215 147C226 143 233 140 241 145C253 151 259 163 274 174C290 186 303 181 319 185C339 190 352 193 367 201L386 214V240H-20Z"
        ></path>
        <path
          className="mountain-shadows"
          d="M100 75C103 94 97 101 99 116C101 130 116 137 111 151C108 162 92 168 83 183C107 179 130 176 145 185C159 172 147 145 140 131C131 114 125 108 121 98C113 89 109 73 100 75ZM238 144C236 154 229 161 234 168C239 176 231 182 226 188C221 194 222 197 215 203C234 198 253 202 274 201C288 198 300 198 308 201C292 188 281 181 274 174C260 163 253 150 238 144Z"
        ></path>
        <g className="rock-folds">
          <path d="M97 87C94 103 84 115 82 127S68 148 64 164M103 107C107 125 119 128 124 145S128 161 134 168M96 137C93 149 83 155 79 169M230 149C223 157 213 162 208 174S197 187 190 195M243 155C250 166 260 171 265 183S278 193 285 200M235 175C240 182 232 189 231 196M146 144C154 151 157 160 164 168M304 188C314 191 321 199 329 204M39 156C34 166 22 173 15 179"></path>
        </g>
        <path
          className="near-ranges"
          d="M-20 216C-1 209 12 196 27 193C44 189 43 179 56 176C70 172 82 190 96 192C115 195 119 180 132 177C145 174 147 158 159 157C173 155 179 175 191 181C206 188 216 191 230 183C242 177 249 163 261 161C275 158 282 177 296 180C310 184 316 176 327 182C343 190 350 188 363 199C371 206 378 206 386 208V240H-20Z"
        ></path>
        <path
          className="lake-mist"
          d="M-10 203C51 191 74 210 124 203S214 195 258 203 328 197 373 203V230H-10Z"
        ></path>
        <path
          className="water-thread"
          d="M-8 213C59 202 84 222 148 214S233 216 285 211 338 217 372 211M18 224C78 216 101 230 160 225S237 222 275 226M209 205C226 203 239 205 252 203"
        ></path>
      </g>
    </svg>
  )
}

function Symbols() {
  return (
    <>
      <svg className="map-symbols" aria-hidden="true" width="0" height="0">
        <defs>
          <path
            id="photo-petals"
            d="M-.22-.22C-.65-1.2 .65-1.2 .22-.22C1.2-.65 1.2 .65 .22 .22C.65 1.2-.65 1.2-.22 .22C-1.2 .65-1.2-.65-.22-.22Z"
          ></path>
        </defs>
      </svg>
    </>
  )
}
